import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { BaseService } from '../../core/services/base.service';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { StorageService, LocalStorageKey, SessionStorageKey } from '../../core/services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {

  private apiUrl = `${environment.apiUrl}/gafi-cpcr`;
  authState = new BehaviorSubject<boolean>(this.hasToken());

  authState$ = this.authState.asObservable();

  constructor(
    protected httpClient: HttpClient,
    private router: Router,
    private storage: StorageService
  ) {
    super(httpClient);
  }
  login(userData: any): Observable<any> {
    const headers = new HttpHeaders({
      'Accept-Language': 'ar'
    });

    return this.http.post<any>(`${this.apiUrl}/authentication/login`, userData, { headers });
  }


  register(userData: any): Observable<any> {
    return this.post<any>(`${this.apiUrl}/user/create`, userData);
  }
  revokeToken(): Observable<any> {
    return this.post<any>(`${this.apiUrl}/authentication/revoke-all`, {});
  }
  logout(): void {
    this.revokeToken().subscribe({
      next: (res) => {
        this.storage.removeLocal(LocalStorageKey.Token);
        const userType = this.storage.getSession(SessionStorageKey.UserType);
        let redirectUri = `${window.location.origin}/home`;
        if (userType == 'Egyptian') {
          this.storage.removeSession(SessionStorageKey.UserType);
          const logoutUrl = `${environment.diLogoutUrl}?client_id=GAFI-Investment&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
          window.location.href = logoutUrl;
        } else {
          window.location.href = redirectUri;
          this.authState.next(false);
        }
      },
      error: (err) => {
        console.error('❌ Revoke failed:', err);
      }
    });

  }

  saveToken(token: string, refreshToken?: string): void {
    this.storage.setLocal(LocalStorageKey.Token, token);
    if (refreshToken) {
      this.storage.setLocal(LocalStorageKey.RefreshToken, refreshToken);
    }
    this.authState.next(true);
  }

  getToken(): string | null {
    return this.storage.getLocal(LocalStorageKey.Token);
  }

  private hasToken(): boolean {
    return this.storage.hasLocal(LocalStorageKey.Token);
  }

  isAuthenticated(): boolean {
    return this.hasToken();
  }


  getUsername(): string {
    const payload = this.decodeToken();
    if (!payload) return '';

    return payload.username || payload.sub || '';
  }
  sendResetPasswordEmail(usernameOrEmail: string, type: string): Observable<any> {

    const headers = new HttpHeaders({
      'Accept-Language': 'ar'
    });

    return this.http.put<any>(
      `${this.apiUrl}/mail/send-mail/${usernameOrEmail}/${type}`,
      {},
      { headers }
    );
  }

  verifyOtp(username: string, otp: string): Observable<any> {
    console.log('Verifying OTP for user:', username, 'with OTP:', otp); // Debug log

    const headers = new HttpHeaders({
      'Accept-Language': 'ar'
    });

    return this.http.get<any>(
      `${this.apiUrl}/authentication/verify-otp/${username}/${otp}`,
      { headers }
    );
  }

  getCurrentUser(): Observable<any> {
    return this.get<any>(`${this.apiUrl}/authentication/current-user`);
  }
  updateUserProfile(id: number, body: any) {
    return this.http.put(`${this.apiUrl}/user/update/${id}`, body);
  }
  changePassword(payload: { username: string, oldPassword: string, newPassword: string }) {
    return this.http.put(`${this.apiUrl}/user/change-password`, payload);
  }

  activateAccount(payload: { username: string; password: string; code: string }): Observable<any> {
    const headers = new HttpHeaders({
      'Accept-Language': 'ar'
    });

    return this.http.put<any>(
      `${this.apiUrl}/user/activate-account`,
      payload,
      { headers }
    );
  }


  resetPassword(payload: { username: string; password: string; code: string }): Observable<any> {
    console.log('Resetting password for user:', payload.username); // Debug log
    const headers = new HttpHeaders({
      'Accept-Language': 'ar'
    });

    return this.http.put<any>(
      `${this.apiUrl}/user/reset-password`,
      payload,
      { headers }
    );
  }

  startLoginByEgyptian(): Observable<any> {
    return this.get<any>(`${this.apiUrl}/auth/citizen/start`);
  }

  loginAsEgyptian(code: any, state: any) {
    return this.post<any>(`${this.apiUrl}/auth/citizen/login?code=${code}&state=${state}`, null);
  }
  getFullName(): string {
    const payload = this.decodeToken();
    if (!payload) return '';

    const firstName = payload.firstName || '';
    const lastName = payload.lastName || '';

    return `${firstName} ${lastName}`.trim();
  }

  getTokenExpiration(token: string): number | null {
    try {
      const decoded = this.decodeToken();

      if (!decoded || !decoded.exp) return null;

      return decoded.exp * 1000;
    } catch (error) {
      console.error('Invalid token', error);
      return null;
    }
  }
  private decodeToken(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      return jwtDecode<any>(token);
    } catch (error) {
      console.warn('Invalid JWT token');
      return null;
    }
  }
  shouldRefreshToken(token: string): boolean {
    const expTime = this.getTokenExpiration(token);
    if (!expTime) return false;
    const now = Date.now();
    const bufferTime = 30 * 60 * 1000
    return expTime - now < bufferTime && expTime - now > 0;
  }
  refreshToken(refreshToken: any): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/authentication/refresh-token`,
      {
        params: { refreshToken }, headers: { skipAuth: 'true' }
      }
    );
  }
}
